const DEFAULT_ORIGIN = "https://your-collector.example.com";

function normalizeOrigin(origin: string): string {
  try {
    const url = new URL(origin);
    return url.origin;
  } catch {
    // Fallback: assume origin is something like "example.com"
    return `https://${origin.replace(/^\/*/, "")}`;
  }
}

export type LoaderType = "sync" | "async";

export interface EmbedSnippetOptions {
  origin?: string;
  loaderType?: LoaderType;
}

function generateSyncLoader(
  origin: string,
  siteId: string,
  collectUrl: string,
  handshakeMessage: string
): string {
  const pixelUrl = `${origin}/cdn/pixel.js`;
  const handshakeLiteral = JSON.stringify(handshakeMessage);

  return `<script>!function(d,src,site,collect,hello){if(d.AR)return;var s=d.createElement("script");s.async=1;s.src=src;s.dataset.siteId=site;s.dataset.collectUrl=collect;s.dataset.handshakeMessage=hello;d.head.appendChild(s);}(document,"${pixelUrl}","${siteId}","${collectUrl}",${handshakeLiteral});</script>`;
}

function generateAsyncLoader(
  origin: string,
  siteId: string,
  collectUrl: string,
  handshakeMessage: string
): string {
  const pixelUrl = `${origin}/cdn/pixel.js`;
  const handshakeLiteral = JSON.stringify(handshakeMessage);

  // RB2B-style async loader: self-contained, prevents duplicates, non-blocking
  return `<script>!function(key,origin){if(window.AR)return;window.AR={loaded:!0};var d=document,s=d.createElement("script");s.async=!0;s.src=origin+"/cdn/pixel.js";s.dataset.siteId=key;s.dataset.collectUrl=origin+"/collect";s.dataset.handshakeMessage=${handshakeLiteral};var t=d.getElementsByTagName("script")[0];t.parentNode.insertBefore(s,t);}("${siteId}","${origin}");</script>`;
}

export function embedSnippet(siteId: string, options: EmbedSnippetOptions = {}): string {
  const resolvedOrigin =
    options.origin ??
    (typeof window !== "undefined" && typeof window.location?.origin === "string"
      ? window.location.origin
      : DEFAULT_ORIGIN);

  const origin = normalizeOrigin(resolvedOrigin);
  const loaderType = options.loaderType ?? "async";
  const collectUrl = `${origin}/collect`;
  const gifUrl = `${origin}/p.gif?s=${siteId}&e=page_view`;
  const handshakeMessage = `AcctRev pixel ready for ${siteId}.`;

  const scriptTag =
    loaderType === "async"
      ? generateAsyncLoader(origin, siteId, collectUrl, handshakeMessage)
      : generateSyncLoader(origin, siteId, collectUrl, handshakeMessage);

  return [scriptTag, `<noscript><img src="${gifUrl}" width="1" height="1" alt="" /></noscript>`].join("\n");
}

export { DEFAULT_ORIGIN as EMBED_SNIPPET_DEFAULT_ORIGIN };
